import { createRoute, Link } from '@tanstack/react-router';
import { Globe, Heart, Check, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { usePreferences, applyTheme, setThemeOverride } from '../hooks/usePreferences';
import { useTranslation } from '../i18n';
import type { Locale } from '../i18n';
import { Button } from '../components/ui/Button';
import { KOFI_URL } from '../constants/external';
import { apiClient } from '../api/client';
import { Route as rootRoute } from './__root';
import styles from './pricing.module.css';
import landingStyles from './index.module.css';

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/pricing',
  component: PricingPage,
});

function PricingPage() {
  const { isAuthenticated } = useAuth();
  const { preferences, setPreference } = usePreferences();
  const { t, locale } = useTranslation();
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('monthly');

  // Force Vivid theme on public pages, restore user's theme on leave
  useEffect(() => {
    setThemeOverride(true);
    applyTheme('vivid');
    return () => {
      setThemeOverride(false);
      applyTheme(preferences.theme);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const toggleLocale = (next: Locale) => setPreference('locale', next);

  const [desktopLoading, setDesktopLoading] = useState(false);

  async function handleDesktopCheckout() {
    setDesktopLoading(true);
    try {
      const { checkoutUrl } = await apiClient.createDesktopCheckout();
      window.location.href = checkoutUrl;
    } catch {
      setDesktopLoading(false);
    }
  }

  function handleProCheckout() {
    if (!isAuthenticated) {
      window.location.href = '/login';
      return;
    }
    // Authenticated users go to the in-app upgrade page with proper checkout flow
    window.location.href = '/upgrade';
  }

  const proPrice = billingPeriod === 'monthly' ? '$5' : '$40';
  const proPeriod = billingPeriod === 'monthly' ? t('pricing.perMonth') : t('pricing.perYear');

  const freeFeatures = [
    { text: t('pricing.freeFeature1'), included: true },
    { text: t('pricing.freeFeature2'), included: true },
    { text: t('pricing.freeFeature3'), included: true },
    { text: t('pricing.freeFeature4'), included: true },
    { text: t('pricing.freeFeature5'), included: true },
    { text: t('pricing.freeFeature6'), included: true },
    { text: t('pricing.freeFeature7'), included: true },
    { text: t('pricing.freeFeature8'), included: true },
    { text: t('pricing.freeFeature9'), included: false },
  ];

  const proFeatures = [
    { text: t('pricing.proFeature1'), included: true },
    { text: t('pricing.proFeature2'), included: true },
    { text: t('pricing.proFeature3'), included: true },
    { text: t('pricing.proFeature4'), included: true },
    { text: t('pricing.proFeature5'), included: true },
    { text: t('pricing.proFeature6'), included: true },
    { text: t('pricing.proFeature7'), included: true },
  ];

  const desktopFeatures = [
    { text: t('pricing.desktopFeature1'), included: true },
    { text: t('pricing.desktopFeature2'), included: true },
    { text: t('pricing.desktopFeature3'), included: true },
    { text: t('pricing.desktopFeature4'), included: true },
    { text: t('pricing.desktopFeature5'), included: true },
    { text: t('pricing.desktopFeature6'), included: true },
    { text: t('pricing.desktopFeature7'), included: true },
    { text: t('pricing.desktopFeature8'), included: true },
  ];

  return (
    <div className={styles.page}>
      {/* Controls */}
      <div className={landingStyles.controls}>
        <div className={landingStyles.langPill} role="group" aria-label="Language">
          <Globe size={13} strokeWidth={1.75} className={landingStyles.langIcon} />
          {(['en', 'he'] as Locale[]).map((loc) => (
            <button
              key={loc}
              className={`${landingStyles.langOption} ${locale === loc ? landingStyles.langOptionActive : ''}`}
              onClick={() => toggleLocale(loc)}
              aria-pressed={locale === loc}
            >
              {loc.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {/* Hero */}
      <section className={styles.hero}>
        <Link to="/" className={styles.backLink}>
          <span className={styles.heroPrompt}>&gt; </span>
          SiliconVault
        </Link>
        <h1 className={styles.heroTitle}>{t('pricing.heroTitle')}</h1>
        <p className={styles.heroTagline}>{t('pricing.heroTagline')}</p>
      </section>

      {/* Pricing grid */}
      <div className={styles.pricingGrid}>
        {/* Free tier */}
        <div className={styles.pricingCard}>
          <div className={styles.tierName}>{t('pricing.freeName')}</div>
          <div className={styles.tierDesc}>{t('pricing.freeDesc')}</div>
          <div className={styles.tierPriceRow}>
            <span className={styles.tierPrice}>{t('pricing.freePrice')}</span>
          </div>
          <div className={styles.priceSubtext}>{t('pricing.freeSubtext')}</div>
          <ul className={styles.featureList}>
            {freeFeatures.map(({ text, included }) => (
              <li key={text} className={styles.featureItem}>
                {included
                  ? <Check size={14} strokeWidth={2.5} className={styles.featureCheck} />
                  : <X size={14} strokeWidth={2.5} className={styles.featureCross} />
                }
                {text}
              </li>
            ))}
          </ul>
          <Link to="/login" className={styles.ctaButton}>
            <Button variant="secondary" style={{ width: '100%' }}>{t('pricing.freeCta')}</Button>
          </Link>
        </div>

        {/* Pro tier */}
        <div className={styles.pricingCardFeatured}>
          <span className={styles.featuredBadge}>{t('pricing.popularBadge')}</span>
          <div className={styles.tierName}>{t('pricing.proName')}</div>
          <div className={styles.tierDesc}>{t('pricing.proDesc')}</div>
          <div className={styles.billingToggle} role="group">
            <button
              className={`${styles.billingOption} ${billingPeriod === 'monthly' ? styles.billingOptionActive : ''}`}
              onClick={() => setBillingPeriod('monthly')}
            >
              {t('pricing.monthly')}
            </button>
            <button
              className={`${styles.billingOption} ${billingPeriod === 'yearly' ? styles.billingOptionActive : ''}`}
              onClick={() => setBillingPeriod('yearly')}
            >
              {t('pricing.yearly')}
            </button>
          </div>
          <div className={styles.tierPriceRow}>
            <span className={styles.tierPrice}>{proPrice}</span>
            <span className={styles.tierPeriod}>{proPeriod}</span>
          </div>
          {billingPeriod === 'yearly' && (
            <div className={styles.saveBadge}>{t('pricing.saveBadge')}</div>
          )}
          <div className={styles.priceSubtext}>
            {billingPeriod === 'yearly' ? t('pricing.yearlySubtext') : t('pricing.monthlySubtext')}
          </div>
          <ul className={styles.featureList}>
            {proFeatures.map(({ text, included }) => (
              <li key={text} className={styles.featureItem}>
                {included
                  ? <Check size={14} strokeWidth={2.5} className={styles.featureCheck} />
                  : <X size={14} strokeWidth={2.5} className={styles.featureCross} />
                }
                {text}
              </li>
            ))}
          </ul>
          <button
            className={styles.ctaButton}
            onClick={handleProCheckout}
            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', width: '100%' }}
          >
            <Button style={{ width: '100%' }}>{t('pricing.proCta')}</Button>
          </button>
        </div>

        {/* Desktop tier */}
        <div className={styles.pricingCard}>
          <div className={styles.tierName}>{t('pricing.desktopName')}</div>
          <div className={styles.tierDesc}>{t('pricing.desktopDesc')}</div>
          <div className={styles.tierPriceRow}>
            <span className={styles.tierPrice}>$50</span>
          </div>
          <div className={styles.priceSubtext}>{t('pricing.desktopSubtext')}</div>
          <ul className={styles.featureList}>
            {desktopFeatures.map(({ text, included }) => (
              <li key={text} className={styles.featureItem}>
                {included
                  ? <Check size={14} strokeWidth={2.5} className={styles.featureCheck} />
                  : <X size={14} strokeWidth={2.5} className={styles.featureCross} />
                }
                {text}
              </li>
            ))}
          </ul>
          <button
            className={styles.ctaButton}
            onClick={handleDesktopCheckout}
            disabled={desktopLoading}
            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', width: '100%' }}
          >
            <Button variant="secondary" style={{ width: '100%' }}>
              {desktopLoading ? 'Loading…' : t('pricing.desktopCta')}
            </Button>
          </button>
        </div>
      </div>

      {/* Footer */}
      <footer className={styles.footer}>
        <span className={styles.footerText}>
          &copy; {new Date().getFullYear()} Asaf Saar. {t('download.footer')}
        </span>
        <span className={styles.footerSep}> | </span>
        <Link to="/pricing" className={styles.footerLink}>{t('pricing.footerLink')}</Link>
        <span className={styles.footerSep}> | </span>
        <Link to="/terms" className={styles.footerLink}>{t('footer.terms')}</Link>
        <span className={styles.footerSep}> | </span>
        <Link to="/privacy" className={styles.footerLink}>{t('footer.privacy')}</Link>
        <span className={styles.footerSep}> | </span>
        <Link to="/refund" className={styles.footerLink}>{t('footer.refund')}</Link>
        <span className={styles.footerSep}> | </span>
        <a
          href={KOFI_URL}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.footerSupport}
        >
          <Heart size={10} strokeWidth={2} />
          {t('support.footerLink')}
        </a>
      </footer>
    </div>
  );
}
